
import { ImageQualityLevel, NavigatorWithConnection, DeviceMemory, ImageTransformParams } from './types';

/**
 * Simple mobile device detection
 */
export const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768;
};

/**
 * Get image quality and width based on quality level and connection
 */
export const getImageQualitySettings = (
  qualityLevel: ImageQualityLevel,
  baseSizes: { width: number; height?: number }
): ImageTransformParams => {
  const nav = navigator as NavigatorWithConnection & DeviceMemory;
  const connection = nav.connection || nav.mozConnection || nav.webkitConnection;
  
  // Consider reduced data mode
  const isSaveData = typeof navigator !== 'undefined' && 
    connection?.saveData === true;
    
  // Consider slow connections
  const isSlowConnection = typeof navigator !== 'undefined' && 
    connection && ['slow-2g', '2g'].includes(connection.effectiveType);
  
  // Check for low memory devices
  const isLowMemoryDevice = typeof nav.deviceMemory !== 'undefined' && nav.deviceMemory < 2;
  
  // Optimize for mobile devices
  const isMobile = isMobileDevice();
  const dpr = typeof window !== 'undefined' ? 
    Math.min(window.devicePixelRatio || 1, 2) : 1;
    
  // Determine best format
  const format = 'webp';
    
  // When using data saver or on very slow connections
  if (isSaveData || isSlowConnection) {
    return {
      width: Math.round(baseSizes.width * 0.6),
      height: baseSizes.height ? Math.round(baseSizes.height * 0.6) : undefined,
      quality: 60,
      format,
      dpr: 1
    };
  }
  
  // Balanced quality levels for better performance
  switch (qualityLevel) {
    case 'low':
      return { 
        width: Math.round(baseSizes.width * 0.8),
        height: baseSizes.height ? Math.round(baseSizes.height * 0.8) : undefined,
        quality: 65,
        format,
        dpr: isMobile ? 1 : 1
      };
    case 'medium':
      return { 
        quality: isMobile ? 75 : 80,
        width: isMobile ? Math.min(baseSizes.width, 600) : Math.min(baseSizes.width, 800),
        height: baseSizes.height ? 
          (isMobile ? Math.min(baseSizes.height, 450) : Math.min(baseSizes.height, 600)) : 
          undefined,
        format,
        dpr: isMobile ? 1 : Math.min(dpr, 1.5)
      };
    case 'high':
      return { 
        quality: isMobile ? 80 : 85,
        width: isMobile ? Math.min(baseSizes.width, 800) : baseSizes.width,
        height: baseSizes.height,
        format,
        dpr: isMobile ? 1 : Math.min(dpr, 2)
      };
    default:
      return { 
        quality: isMobile ? 75 : 80, 
        width: isMobile ? Math.min(baseSizes.width, 600) : Math.min(baseSizes.width, 800),
        height: baseSizes.height ? 
          (isMobile ? Math.min(baseSizes.height, 450) : Math.min(baseSizes.height, 600)) : 
          undefined,
        format,
        dpr: isMobile ? 1 : Math.min(dpr, 1.5)
      };
  }
};
